import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { engineerAPI } from '../lib/api';
import {
  Clock, Play, Square, MapPin, History, Loader2, CheckCircle2, Briefcase,
  AlertTriangle, Lock, ChevronDown, Plus, X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDuration = (totalSeconds) => {
  if (!totalSeconds || totalSeconds < 0) return '0h 0m';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m}m`;
};

const fmtClock = (totalSeconds) => {
  if (!totalSeconds || totalSeconds < 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

const fmtDate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return '—';
  try {
    return new Date(yyyy_mm_dd).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return yyyy_mm_dd; }
};

const getLocation = () => new Promise((resolve) => {
  if (!('geolocation' in navigator)) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
    () => resolve(null),
    { timeout: 5000, enableHighAccuracy: false }
  );
});

const isClockInLocked = (policy) => {
  if (!policy) return false;
  // Block before 06:00 London time. The browser may be in a different TZ —
  // use Intl with Europe/London to get the real local hour.
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', hour: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    return hour < (policy.earliest_clock_in_hour ?? 6);
  } catch { return false; }
};

/**
 * Engineer Time Clock (Connecteam-style).
 *
 * - Big circular clock face with live elapsed time when on shift.
 * - Hard-blocks Clock In before 06:00 (Europe/London).
 * - Out-of-standard-hours shifts auto-flagged for admin approval.
 * - Engineer selects scheduled jobs at clock-in and allocates time across
 *   them at clock-out (or any time during the shift).
 */
export const TimeClockWidget = () => {
  const [status, setStatus] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState({}); // {jobKey: true}
  const [customJobs, setCustomJobs] = useState([]); // [{customer_id, customer_name, label}]
  const [customDraft, setCustomDraft] = useState({ customer_id: '', label: '' });
  const [allocations, setAllocations] = useState({}); // {jobKey: minutes}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [history, setHistory] = useState(null);
  const [now, setNowTick] = useState(Date.now());
  const tickRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [statusR, policyR, jobsR, histR] = await Promise.all([
        engineerAPI.timeClockStatus(),
        engineerAPI.timeClockPolicy().catch(() => ({ data: null })),
        engineerAPI.scheduledJobs().catch(() => ({ data: [] })),
        engineerAPI.timeClockHistory(14).catch(() => ({ data: null })),
      ]);
      setStatus(statusR.data);
      setPolicy(policyR.data);
      setScheduledJobs(jobsR.data || []);
      setHistory(histR.data);

      // Hydrate allocation drafts from open shift
      const open = statusR.data?.open_shift;
      if (open?.job_allocations) {
        const sel = {};
        const alloc = {};
        open.job_allocations.forEach(a => {
          const key = `${a.customer_id}::${a.label}`;
          sel[key] = true;
          alloc[key] = Math.floor((a.seconds || 0) / 60);
        });
        setSelectedJobIds(sel);
        setAllocations(alloc);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (status?.is_clocked_in) {
      tickRef.current = setInterval(() => setNowTick(Date.now()), 1000);
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [status?.is_clocked_in]);

  const openShift = status?.open_shift;
  const openElapsed = (() => {
    if (!openShift?.clock_in_at) return 0;
    try { return Math.max(0, Math.floor((now - new Date(openShift.clock_in_at).getTime()) / 1000)); } catch { return 0; }
  })();
  const totalToday = (status?.today_logged_seconds || 0) + (status?.is_clocked_in ? openElapsed : 0);
  const isOn = !!status?.is_clocked_in;
  const lockedOut = !isOn && isClockInLocked(policy);

  // Local London time hint
  const londonClock = (() => {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date());
    } catch { return ''; }
  })();

  // Standard hour check for current London time
  const inStandardHours = (() => {
    if (!policy || !londonClock) return true;
    const [h, m] = londonClock.split(':').map(Number);
    const minutes = h * 60 + m;
    const [sh, sm] = (policy.standard_start || '07:30').split(':').map(Number);
    const [eh, em] = (policy.standard_end || '17:00').split(':').map(Number);
    return minutes >= (sh * 60 + sm) && minutes < (eh * 60 + em);
  })();

  // Combine scheduled + custom jobs for selection
  const allJobOptions = [
    ...scheduledJobs.map(j => ({
      key: `${j.customer_id}::${j.job_type || 'Install'} ${j.scheduled_date || ''}`.trim(),
      customer_id: j.customer_id,
      customer_name: j.customer_name,
      label: `${j.job_type || 'Install'} — ${fmtDate(j.scheduled_date)}`,
      sub: j.address,
    })),
    ...customJobs.map(c => ({
      key: `${c.customer_id}::${c.label}`,
      customer_id: c.customer_id,
      customer_name: c.customer_name,
      label: c.label,
      sub: 'Custom job',
      isCustom: true,
    })),
  ];

  const toggleJob = (job) => setSelectedJobIds(prev => ({ ...prev, [job.key]: !prev[job.key] }));

  const addCustomJob = () => {
    const id = customDraft.customer_id.trim();
    const label = customDraft.label.trim();
    if (!id || !label) return;
    setCustomJobs(prev => [...prev, { customer_id: id, label, customer_name: id }]);
    setCustomDraft({ customer_id: '', label: '' });
  };

  const removeCustomJob = (key) => {
    setCustomJobs(prev => prev.filter(c => `${c.customer_id}::${c.label}` !== key));
    setSelectedJobIds(prev => { const next = { ...prev }; delete next[key]; return next; });
    setAllocations(prev => { const next = { ...prev }; delete next[key]; return next; });
  };

  const buildAllocationsPayload = () => {
    return allJobOptions
      .filter(j => selectedJobIds[j.key])
      .map(j => ({
        customer_id: j.customer_id,
        customer_name: j.customer_name,
        label: j.label,
        seconds: Math.max(0, Math.round((allocations[j.key] || 0) * 60)),
      }));
  };

  const handleToggle = async () => {
    setBusy(true);
    try {
      const loc = await getLocation();
      const allocPayload = buildAllocationsPayload();
      const body = { note: note.trim(), job_allocations: allocPayload, ...(loc || {}) };
      if (isOn) {
        const r = await engineerAPI.clockOut(body);
        if (r.data?.approval_status === 'pending') {
          toast.success('Clocked out. Overtime shift flagged for admin approval.');
        } else {
          toast.success(`Clocked out — ${fmtDuration(r.data?.duration_seconds || openElapsed)} logged`);
        }
      } else {
        await engineerAPI.clockIn(body);
        toast.success(inStandardHours ? 'Clocked in. Have a productive shift!' : 'Clocked in — outside standard hours (will need admin approval at clock-out)');
      }
      setNote('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update time clock');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="time-clock-loading">
        <CardContent className="p-8 flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const totalAllocatedMins = Object.values(allocations).reduce((a, b) => a + (Number(b) || 0), 0);
  const elapsedMins = Math.floor(openElapsed / 60);
  const allocOverflow = isOn && totalAllocatedMins > elapsedMins + 5; // 5min tolerance

  return (
    <Card className={`border-2 overflow-hidden transition-colors ${isOn ? 'border-emerald-400 bg-gradient-to-b from-emerald-50/50 to-white' : 'border-slate-200'}`} data-testid="time-clock-widget">
      <CardContent className="p-0">
        {/* Hero clock face */}
        <div className={`px-6 py-8 text-center ${isOn ? 'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white' : 'bg-slate-50 text-slate-900'}`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock className="w-4 h-4 opacity-80" />
            <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
              {isOn ? 'On the Clock' : 'Off the Clock'}
            </span>
            {policy && (
              <Badge className={`ml-2 text-[10px] ${isOn ? 'bg-white/20 text-white border-white/30' : 'bg-slate-200 text-slate-600 border-slate-300'} border`}>
                {policy.tz?.replace('Europe/', '')} · {londonClock}
              </Badge>
            )}
          </div>
          <div className="text-5xl sm:text-6xl font-bold font-mono tabular-nums tracking-tight" data-testid="time-clock-elapsed">
            {isOn ? fmtClock(openElapsed) : fmtClock(0)}
          </div>
          <div className={`mt-3 text-xs ${isOn ? 'text-white/90' : 'text-slate-500'}`}>
            {isOn ? (
              <>Started {fmtTime(openShift?.clock_in_at)} · Today total <strong>{fmtDuration(totalToday)}</strong></>
            ) : (
              <>Today's total <strong>{fmtDuration(totalToday)}</strong> · {status?.todays_shifts?.length || 0} shift{status?.todays_shifts?.length === 1 ? '' : 's'}</>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Out-of-hours warnings */}
          {lockedOut && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-900 text-xs" data-testid="time-clock-locked">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Clock-in is locked until 06:00 London. Current time {londonClock}.</span>
            </div>
          )}
          {!lockedOut && !inStandardHours && policy && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Outside standard hours ({policy.standard_start}–{policy.standard_end}).
                {isOn ? ' Shift will need admin approval at clock-out.' : ' Clocking in now flags the shift for admin approval.'}
              </span>
            </div>
          )}
          {isOn && openShift?.is_overtime && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Overtime shift — pending admin approval on clock-out.</span>
            </div>
          )}

          {/* Job allocations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Jobs for this Shift
              </p>
              {isOn && (
                <span className={`text-[10px] ${allocOverflow ? 'text-rose-600' : 'text-slate-500'}`}>
                  {totalAllocatedMins}m allocated of {elapsedMins}m
                </span>
              )}
            </div>
            {allJobOptions.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No upcoming jobs scheduled for the next 14 days. Add a custom job below.</p>
            ) : (
              <div className="space-y-1.5">
                {allJobOptions.map(j => (
                  <div key={j.key} className="rounded-lg border border-slate-200 p-2.5 bg-white hover:border-slate-300 transition-colors" data-testid={`job-option-${j.customer_id}`}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox checked={!!selectedJobIds[j.key]} onCheckedChange={() => toggleJob(j)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{j.customer_name || 'Customer'}</p>
                        <p className="text-[11px] text-slate-500 truncate">{j.label}{j.sub ? ` · ${j.sub}` : ''}</p>
                      </div>
                      {j.isCustom && (
                        <button type="button" onClick={(e) => { e.preventDefault(); removeCustomJob(j.key); }} className="text-slate-400 hover:text-rose-500" data-testid={`job-remove-${j.customer_id}`}>
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </label>
                    {selectedJobIds[j.key] && isOn && (
                      <div className="mt-2 pl-6 flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          step="5"
                          value={allocations[j.key] || ''}
                          onChange={(e) => setAllocations(prev => ({ ...prev, [j.key]: e.target.value }))}
                          placeholder="0"
                          className="h-7 text-xs w-24"
                          data-testid={`job-alloc-${j.customer_id}`}
                        />
                        <span className="text-[11px] text-slate-500">minutes on this job</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add custom job — for jobs not on the schedule */}
            <details className="mt-2">
              <summary className="text-[11px] text-blue-600 cursor-pointer hover:text-blue-700 flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add custom job
              </summary>
              <div className="mt-2 flex gap-2">
                <Input
                  placeholder="Customer ID / postcode"
                  value={customDraft.customer_id}
                  onChange={(e) => setCustomDraft({ ...customDraft, customer_id: e.target.value })}
                  className="h-7 text-xs flex-1"
                  data-testid="custom-job-id"
                />
                <Input
                  placeholder="Job description"
                  value={customDraft.label}
                  onChange={(e) => setCustomDraft({ ...customDraft, label: e.target.value })}
                  className="h-7 text-xs flex-1"
                  data-testid="custom-job-label"
                />
                <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={addCustomJob} disabled={!customDraft.customer_id.trim() || !customDraft.label.trim()} data-testid="custom-job-add">
                  Add
                </Button>
              </div>
            </details>
          </div>

          {/* Note + toggle */}
          <Textarea
            placeholder={isOn ? 'Optional note (what did you work on?)' : 'Optional note before starting'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="text-sm"
            data-testid="time-clock-note"
          />
          <Button
            onClick={handleToggle}
            disabled={busy || lockedOut}
            className={`w-full h-14 text-base font-semibold transition-transform active:scale-[0.99] ${
              lockedOut
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : isOn
                  ? 'bg-rose-600 hover:bg-rose-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
            data-testid="time-clock-toggle"
          >
            {busy ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : lockedOut ? (
              <Lock className="w-5 h-5 mr-2" />
            ) : isOn ? (
              <Square className="w-5 h-5 mr-2" />
            ) : (
              <Play className="w-5 h-5 mr-2" />
            )}
            {lockedOut ? 'Locked until 06:00' : busy ? 'Working…' : isOn ? 'Clock Out' : 'Clock In'}
          </Button>
          <p className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
            <MapPin className="w-2.5 h-2.5" />
            GPS captured · Standard hours {policy?.standard_start || '07:30'}–{policy?.standard_end || '17:00'}
          </p>

          {/* Today's shifts */}
          {status?.todays_shifts?.length > 0 && (
            <div className="border-t border-slate-200 pt-3">
              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Today</p>
              <div className="space-y-1">
                {status.todays_shifts.map((s, idx) => (
                  <div key={s.id || idx} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded bg-slate-50">
                    <span className="font-mono">
                      {fmtTime(s.clock_in_at)} → {s.clock_out_at ? fmtTime(s.clock_out_at) : <span className="text-emerald-600 font-semibold">live</span>}
                    </span>
                    <span className="flex items-center gap-1.5 tabular-nums text-slate-700 font-medium">
                      {s.is_overtime && <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[9px] px-1 py-0">OT</Badge>}
                      {fmtDuration(s.clock_out_at ? s.duration_seconds : openElapsed)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 14-day history */}
          {history?.daily_totals?.length > 0 && (
            <details className="border-t border-slate-200 pt-3" data-testid="time-clock-history">
              <summary className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide cursor-pointer flex items-center gap-1.5 hover:text-slate-900">
                <History className="w-3.5 h-3.5" /> Last 14 days
                <ChevronDown className="w-3 h-3 ml-auto" />
              </summary>
              <div className="mt-2 space-y-1">
                {history.daily_totals.map(d => (
                  <div key={d.date} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded bg-white border border-slate-100" data-testid={`history-row-${d.date}`}>
                    <span className="text-slate-700">{fmtDate(d.date)}</span>
                    <span className="flex items-center gap-2 tabular-nums">
                      <span className="text-slate-400">{d.shifts} shift{d.shifts === 1 ? '' : 's'}</span>
                      <span className="font-semibold text-slate-900">{fmtDuration(d.total_seconds)}</span>
                      {d.has_open && <CheckCircle2 className="w-3 h-3 text-emerald-500" title="Open shift" />}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded bg-slate-100 font-semibold">
                  <span>14-day total</span>
                  <span className="tabular-nums">{fmtDuration(history.daily_totals.reduce((acc, d) => acc + (d.total_seconds || 0), 0))}</span>
                </div>
              </div>
            </details>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeClockWidget;
